import moment from "moment";
import { Box, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import useMobileScreen from "../../../hooks/useMobileScreen";
import useIsReactNativeWebView from "../../../hooks/useIsReactNativeWebView";

import { rupeeDisplay } from "../../../helpers/rupee";
import useGetDepartments from "../../../hooks/department";
import { RetirementFundType } from "../../../constants/payroll";
import ListWithNestedHeader from "../../../components/ListWithNestedHeader/Index";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import { TEarningDeductionComponent, TSalary } from "../../../interfaces/SalaryInterface";
import { useSalaries } from "../../../hooks/query/useSalaries";
import { SalaryListHeader } from "../../Payroll/Salary/Components/SalaryListHeader";
import { Employee } from "../../../interfaces/User";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";

export default function PayrollReport(): React.ReactNode {
  const departments = useGetDepartments();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();

  const currentBs = convertADtoBS(new Date());

  const [selectedEmployee, setSelectedEmployee] = useState<Employee | ServiceProvider | null>(null);

  const [filters, setFilters] = useState<{
    employeeIds: number[];
    serviceProviderIds: number[];
    payrollYear: number;
    payrollMonth: number;
  }>({
    employeeIds: [],
    serviceProviderIds: [],
    payrollYear: currentBs.bsYear,
    payrollMonth: currentBs.bsMonth
  });

  const { data: salaryList = [], isLoading: loading } = useSalaries(filters);

  const uniqueLeaveKeys = useMemo(() => {
    const keys = new Set<string>();
    salaryList.forEach((row) => {
      if (row.leaveSummary) {
        Object.keys(row.leaveSummary).forEach((k) => keys.add(k));
      }
    });
    return Array.from(keys);
  }, [salaryList]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ py: 0.5 }}>
        <SalaryListHeader
          showFilterOnly
          selectedEmployee={selectedEmployee}
          setSelectedEmployee={setSelectedEmployee}
          filter={filters}
          setFilter={setFilters}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          width: "100%",
          overflow: "auto"
        }}
      >
        <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "2500px" : "auto" }}>
          <ListWithNestedHeader
            dataTestmation="payrollList"
            emptyViewContent="There are no items to show."
            data={salaryList}
            isLoading={loading}
            columns={[
              [
                {
                  key: "symbolNumber",
                  label: "SN",
                  formatter: (_, index) => <Typography>{index + 1}</Typography>
                },
                {
                  key: "employeeDetail",
                  label: "Employee Detail",
                  children: [
                    {
                      key: "id",
                      label: "Employee ID",
                      formatter: (row) => (
                        <Typography>{row.employeeId || row.serviceProviderId}</Typography>
                      )
                    },
                    {
                      key: "name",
                      label: "Name",
                      formatter: (row) => {
                        let name = "N.A.";
                        if (row.employee) {
                          name = `${row.employee.firstName} ${row.employee.lastName}`;
                        } else if (row.serviceProvider) {
                          name = `${row.serviceProvider.firstName} ${row.serviceProvider.lastName}`;
                        }
                        return (
                          <Typography component="span" variant="body2">
                            {name}
                          </Typography>
                        );
                      }
                    },
                    {
                      key: "role",
                      label: "Role",
                      formatter: (row) => (
                        <Typography component="span" variant="body2">
                          {row.employeeId ? "Employee" : "Service Provider"}
                        </Typography>
                      )
                    },
                    {
                      key: "datOfJoining",
                      label: "Date of Joining",
                      formatter: (row) => {
                        let doj = "N.A.";
                        if (row?.employeeId && row.employee?.dateOfJoining) {
                          doj = convertADtoBS(moment(row.employee.dateOfJoining)).formatted4;
                        }

                        if (row?.serviceProviderId && row?.serviceProvider?.dateOfJoining) {
                          doj = convertADtoBS(moment(row.serviceProvider.dateOfJoining)).formatted4;
                        }
                        return <Typography>{doj}</Typography>;
                      }
                    },
                    {
                      key: "department",
                      label: "Department",
                      formatter: (row) => {
                        let departmentName = "N.A.";
                        const employee = row?.employee || row?.serviceProvider;

                        if (employee && employee?.departmentId) {
                          const department = departments.find(
                            (dept) => String(dept.id) === String(employee.departmentId)
                          );

                          if (department) departmentName = department.name;
                        }

                        return <Typography>{departmentName}</Typography>;
                      }
                    },
                    {
                      key: "designation",
                      label: "Designation",
                      formatter: (row) => {
                        let designation = "N.A.";
                        if (row.employee?.designation) designation = row.employee?.designation;
                        if (row.serviceProvider?.designation)
                          designation = row.serviceProvider?.designation;

                        return <Typography>{designation}</Typography>;
                      }
                    }
                    // TODO: employee category i.e. part time, full time
                  ]
                },
                {
                  key: "calendarSummary",
                  label: "Calendar Summary",
                  children: [
                    {
                      key: "calendarDays",
                      label: "Calendar Days",
                      formatter: (row) => {
                        const calendarDays = row.calendarSummary.calendarDays ?? 0;
                        return <Typography>{calendarDays}</Typography>;
                      }
                    },
                    {
                      key: "businessDays",
                      label: "Working Days",
                      formatter: (row) => {
                        const businessDays = row.calendarSummary.businessDays ?? 0;
                        return <Typography>{businessDays}</Typography>;
                      }
                    },
                    {
                      key: "defaultWeeklyOffs",
                      label: "Weekly Offs",
                      formatter: (row) => {
                        const defaultWeeklyOffs = row.calendarSummary.defaultWeeklyOffs ?? 0;
                        return <Typography>{defaultWeeklyOffs}</Typography>;
                      }
                    },
                    {
                      key: "publicHolidays",
                      label: "Public Holidays",
                      formatter: (row) => {
                        const publicHolidays = row.calendarSummary.publicHolidays ?? 0;
                        return <Typography>{publicHolidays}</Typography>;
                      }
                    },
                    {
                      key: "unpaidLeaveDays",
                      label: "Unpaid Leave Days",
                      formatter: (row) => {
                        const unpaidLeaveDays = row.calendarSummary.unpaidLeaveDays ?? 0;
                        return <Typography>{unpaidLeaveDays}</Typography>;
                      }
                    },
                    {
                      key: "payableDays",
                      label: "Payable Days",
                      formatter: (row) => {
                        const payableDays = row.calendarSummary.payableDays ?? 0;
                        return <Typography>{payableDays}</Typography>;
                      }
                    },
                    {
                      key: "employmentDays",
                      label: "Employment Days",
                      formatter: (row) => {
                        const employmentDays = row.calendarSummary.employmentDays ?? 0;
                        return <Typography>{employmentDays}</Typography>;
                      }
                    }
                  ]
                },
                {
                  key: "attendanceSummary",
                  label: "Attendance Summary",
                  children: [
                    {
                      key: "leave",
                      label: "Leaves",
                      children: uniqueLeaveKeys.map((leaveKey) => ({
                        key: `leave_${leaveKey}`,
                        label:
                          leaveKey.charAt(0).toUpperCase() +
                          leaveKey.slice(1).replace(/([A-Z])/g, " $1") +
                          (leaveKey.toLowerCase().endsWith("leave") ||
                          leaveKey.toLowerCase().endsWith("leaves")
                            ? ""
                            : " Leave"),
                        formatter: (row: TSalary) => {
                          const leaveVal = row.leaveSummary?.[leaveKey] ?? 0;
                          return <Typography>{leaveVal}</Typography>;
                        }
                      }))
                    }
                  ]
                },
                {
                  key: "Gross earnings",
                  label: "Gross Earningns",
                  children: [
                    {
                      key: "basicSalary",
                      label: "Basic Salary",
                      formatter: (row) => {
                        const basicSalary = row.earnings.basicSalary ?? 0;
                        return <Typography>{rupeeDisplay(basicSalary)}</Typography>;
                      }
                    },

                    // allowances
                    {
                      key: "allowances",
                      label: "Allowances",
                      children: [
                        {
                          key: "houseRentAllowance",
                          label: "House Rent Allowance",
                          formatter: (row) => {
                            const houseRentAllowance =
                              row.earnings.allowances?.houseRentAllowance?.value ?? 0;
                            return <Typography>{rupeeDisplay(houseRentAllowance)}</Typography>;
                          }
                        },
                        {
                          key: "conveyanceAllowance",
                          label: "Conveyance Allowance",
                          formatter: (row) => {
                            const conveyanceAllowance =
                              row.earnings.allowances?.conveyanceAllowance?.value ?? 0;
                            return <Typography>{rupeeDisplay(conveyanceAllowance)}</Typography>;
                          }
                        },
                        {
                          key: "dearnesAllowance",
                          label: "Dearness Allowance",
                          formatter: (row) => {
                            const dearnesAllowance =
                              row.earnings.allowances?.dearnesAllowance?.value ?? 0;
                            return <Typography>{rupeeDisplay(dearnesAllowance)}</Typography>;
                          }
                        },
                        {
                          key: "festivalAllowance",
                          label: "Other Allowance",
                          formatter: (row) => {
                            const festivalAllowance =
                              row.earnings.allowances?.festivalAllowance?.value ?? 0;
                            return <Typography>{rupeeDisplay(festivalAllowance)}</Typography>;
                          }
                        },
                        {
                          key: "otherAllowance",
                          label: "Other Allowance",
                          formatter: (row) => {
                            const otherAllowance =
                              row.earnings.allowances?.otherAllowance?.value ?? 0;
                            return <Typography>{rupeeDisplay(otherAllowance)}</Typography>;
                          }
                        }
                      ]
                    },

                    // bonuses
                    {
                      key: "bonuses",
                      label: "Bonuses",
                      children: [
                        {
                          key: "festivalBonus",
                          label: "Festival Bonus",
                          formatter: (row) => {
                            const festivalBonus =
                              row.earnings.allowances?.festivalBonus?.value ?? 0;
                            return <Typography>{rupeeDisplay(festivalBonus)}</Typography>;
                          }
                        },
                        {
                          key: "otherBonus",
                          label: "Other Bonus",
                          formatter: (row) => {
                            const otherBonus = row.earnings.allowances?.otherBonus?.value ?? 0;
                            return <Typography>{rupeeDisplay(otherBonus)}</Typography>;
                          }
                        }
                      ]
                    },

                    // employer contributions
                    {
                      key: "employerContributions",
                      label: "Employer Contributions",
                      children: [
                        {
                          key: "providentFund",
                          label: "Provident Fund",
                          formatter: (row) => {
                            const providentFund =
                              row?.salarySummary?.retirementContributions?.summary?.pfContributions
                                ?.employer ?? 0;
                            return <Typography>{rupeeDisplay(providentFund)}</Typography>;
                          }
                        },
                        {
                          key: "socialSecurityFund",
                          label: "Social Security Fund",
                          formatter: (row) => {
                            const socialSecurityFund =
                              row.salarySummary?.retirementContributions?.summary?.ssfContributions
                                ?.employer ?? 0;
                            return <Typography>{rupeeDisplay(socialSecurityFund)}</Typography>;
                          }
                        }
                      ]
                    },

                    // TODO: arriers (the unpaid amounts to employee)
                    {
                      key: "grossEarningTotal",
                      label: "Gross Earning Total (TODO)",
                      formatter: () => <Typography>-</Typography>
                    }
                  ]
                },
                {
                  key: "allDeductions",
                  label: "All Deductions",
                  children: [
                    // employee contribution
                    {
                      key: "employeeContributions",
                      label: "Employee Contributions",
                      children: [
                        {
                          key: "providentFund",
                          label: "Provident Fund",
                          formatter: (row) => {
                            const providentFund =
                              row.salarySummary?.retirementContributions.summary?.pfContributions
                                ?.employee ?? 0;
                            return <Typography>{rupeeDisplay(providentFund)}</Typography>;
                          }
                        },
                        {
                          key: "socialSecurityFund",
                          label: "Social Security Fund",
                          formatter: (row) => {
                            const socialSecurityFund =
                              row.salarySummary?.retirementContributions.summary?.ssfContributions
                                ?.employee ?? 0;
                            return <Typography>{rupeeDisplay(socialSecurityFund)}</Typography>;
                          }
                        }
                      ]
                    },
                    {
                      key: "leaveDeduction",
                      label: "Leave Deduction",
                      formatter: (row) => {
                        const leaveDeduction = row.deductions?.leaveDeduction?.value ?? 0;
                        return <Typography>{rupeeDisplay(leaveDeduction)}</Typography>;
                      }
                    },
                    {
                      key: "otherDeduction",
                      label: "Other Deduction",
                      formatter: (row) => {
                        const otherDeduction = row.deductions?.otherDeduction?.value ?? 0;
                        return <Typography>{rupeeDisplay(otherDeduction)}</Typography>;
                      }
                    },
                    {
                      key: "taxDeduction",
                      label: "Tax Deduction",
                      formatter: (row) => {
                        const taxDeduction = row?.salarySummary?.tax?.netCurrentMonthTax ?? 0;
                        return <Typography>{rupeeDisplay(taxDeduction)}</Typography>;
                      }
                    },
                    // TODO: advance salary, employee loan
                    {
                      key: "totalDeduction",
                      label: "Total Deduction",
                      formatter: (row) => {
                        const totalDeductions = Object.values(row.deductions).reduce(
                          (sum: number, deduction: TEarningDeductionComponent) =>
                            sum + (deduction?.value || 0),
                          0
                        );

                        const employeeMonthlyContribution =
                          (row?.retirementFundType === RetirementFundType.PROVIDENT_FUND
                            ? Number(
                                row.salarySummary?.retirementContributions?.summary.pfContributions
                                  ?.employee ?? 0
                              )
                            : 0) +
                          (row?.retirementFundType === RetirementFundType.SOCIAL_SECURITY_FUND
                            ? Number(
                                row.salarySummary?.retirementContributions?.summary.ssfContributions
                                  ?.employee ?? 0
                              )
                            : 0);

                        const taxDeduction = row?.salarySummary?.tax?.netCurrentMonthTax ?? 0;

                        const allDeductions =
                          totalDeductions + employeeMonthlyContribution + taxDeduction;

                        return <Typography>{rupeeDisplay(allDeductions)}</Typography>;
                      }
                    }
                  ]
                },
                {
                  key: "netSalary",
                  label: "Net Salary",
                  formatter: (row) => {
                    const netMonthlySalary = row?.salarySummary?.salary?.netMonthlySalary ?? 0;
                    return <Typography>{rupeeDisplay(netMonthlySalary)}</Typography>;
                  }
                },
                {
                  key: "totalPayable",
                  label: "Total Payable",
                  formatter: (row) => {
                    const totalPayable =
                      row?.salarySummary?.salary?.totalPayable ??
                      row?.salarySummary?.salary?.netMonthlySalary ??
                      0;
                    return <Typography>{rupeeDisplay(totalPayable)}</Typography>;
                  }
                }
              ]
            ]}
          />
        </Box>
      </Box>
    </Box>
  );
}
